import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { CLIENTS } from "@/constants/testIds";
import { toast, Toaster } from "sonner";
import { Plus, LayoutGrid, Rows, Search as SearchIcon, ExternalLink, X } from "lucide-react";
import { formatPhone, phoneDigits, isValidEmail, normalizeUrl } from "@/lib/formHelpers";
import AddClientModal from "@/pages/clients/AddClientModal";

const STATUS_COLUMNS = [
  { key: "prospect", label: "Prospect" },
  { key: "trial", label: "Trial" },
  { key: "active", label: "Active" },
  { key: "paused", label: "Paused" },
  { key: "churned", label: "Churned" },
];

const PLAN_LABELS = { starter: "Starter", front_office: "Front Office", multi_location: "Multi-Location", custom: "Custom" };

const EMPTY_FORM = {
  business_name: "", contact_name: "", email: "", phone: "",
  industry: "", city: "", state: "", address: "", zip: "",
  website_url: "", notes: "",
  status: "prospect", plan_tier: "starter", mrr: 397,
};

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("kanban");
  const [query, setQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "";

  const load = async () => {
    try {
      const [{ data: clientsRes }, { data: statsRes }, { data: indRes }] = await Promise.all([
        api.get("/clients"),
        api.get("/clients/stats"),
        api.get("/industries"),
      ]);
      setClients(clientsRes.clients || []);
      setStats(statsRes);
      setIndustries(indRes.industries || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Auto-adjust MRR when plan tier changes (only if MRR was still on a default)
  useEffect(() => {
    const defaults = { starter: 397, front_office: 697, multi_location: 997 };
    if (defaults[form.plan_tier] && [0, 397, 697, 997].includes(Number(form.mrr))) {
      setForm((f) => ({ ...f, mrr: defaults[form.plan_tier] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.plan_tier]);

  const filtered = useMemo(() => {
    let list = clients;
    if (statusFilter) list = list.filter((c) => c.status === statusFilter);
    if (!query) return list;
    const q = query.toLowerCase();
    return list.filter((c) =>
      c.business_name?.toLowerCase().includes(q) ||
      c.contact_name?.toLowerCase().includes(q) ||
      c.city?.toLowerCase().includes(q) ||
      c.industry?.toLowerCase().includes(q)
    );
  }, [clients, query, statusFilter]);

  const byStatus = useMemo(() => {
    const map = Object.fromEntries(STATUS_COLUMNS.map((c) => [c.key, []]));
    filtered.forEach((c) => { (map[c.status] || map["prospect"]).push(c); });
    return map;
  }, [filtered]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim()) { toast.error("Business name is required."); return; }
    if (form.email && !isValidEmail(form.email)) { toast.error("Please enter a valid email address."); return; }
    const rawPhone = phoneDigits(form.phone);
    if (form.phone && rawPhone.length > 0 && rawPhone.length !== 10) {
      toast.error("Phone must be a 10-digit US number, or left empty.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        phone: rawPhone ? formatPhone(rawPhone) : "",
        website_url: normalizeUrl(form.website_url),
        mrr: Number(form.mrr) || 0,
      };
      await api.post("/clients", payload);
      toast.success(`Client "${form.business_name}" added.`);
      setShowForm(false);
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid={CLIENTS.container} className="p-10">
      <Toaster position="top-right" richColors />
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="overline mb-2">Back office</div>
          <h1 className="font-display text-4xl font-black tracking-tight">Clients</h1>
          <p className="text-neutral-600 mt-2">
            Everyone paying you or on trial. Track the pipeline from prospect to churn.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid={CLIENTS.toggleView}
            onClick={() => setView(view === "kanban" ? "table" : "kanban")}
            className="lf-btn-outline flex items-center gap-2"
          >
            {view === "kanban" ? <><Rows className="w-4 h-4" /> Table</> : <><LayoutGrid className="w-4 h-4" /> Kanban</>}
          </button>
          <button
            data-testid={CLIENTS.addBtn}
            onClick={() => setShowForm(true)}
            className="lf-btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add client
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total clients" value={stats.total_clients} />
          <StatCard label="Active" value={stats.active_count} />
          <StatCard label="Trial" value={stats.by_status?.trial ?? 0} />
          <StatCard label="MRR" value={`$${(stats.mrr || 0).toLocaleString()}`} accent />
        </div>
      )}

      <div className="mb-6 relative max-w-md">
        <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, contact, city, industry…"
          className="w-full pl-10 pr-4 py-2.5 border border-neutral-300 bg-white focus:outline-none focus:border-black text-sm"
        />
      </div>

      {statusFilter && (
        <div className="mb-6 inline-flex items-center gap-2 border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs" data-testid="clients-filter-pill">
          <span className="font-mono uppercase tracking-wider text-amber-800">Filter:</span>
          <span className="text-amber-900 font-semibold">status = {statusFilter}</span>
          <button onClick={() => setSearchParams({})} className="text-amber-800 hover:text-amber-950 ml-1" data-testid="clients-filter-clear">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-neutral-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-neutral-300 p-16 text-center bg-white">
          <div className="font-display text-2xl mb-2">No clients yet.</div>
          <p className="text-neutral-500 mb-6">Add your first client or promote a lead from the pipeline.</p>
          <div className="flex justify-center gap-3">
            <button onClick={() => setShowForm(true)} className="lf-btn-primary">Add a client</button>
            <Link to="/leads" className="lf-btn-outline">Open lead pipeline →</Link>
          </div>
        </div>
      ) : view === "kanban" ? (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {STATUS_COLUMNS.map((col) => (
            <div key={col.key} data-testid={CLIENTS.column(col.key)} className="lf-kanban-col p-3">
              <div className="flex items-center justify-between mb-3">
                <div className="overline">{col.label}</div>
                <span className="text-xs font-mono text-neutral-500">{byStatus[col.key].length}</span>
              </div>
              <div className="flex flex-col gap-2">
                {byStatus[col.key].map((c) => (
                  <Link
                    key={c.id}
                    data-testid={CLIENTS.card(c.id)}
                    to={`/clients/${c.id}`}
                    className="lf-kanban-card block"
                  >
                    <div className="font-medium text-sm mb-1">{c.business_name}</div>
                    <div className="text-xs text-neutral-500 mb-2">{c.city}{c.city && c.industry ? " · " : ""}{c.industry}</div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-mono uppercase tracking-wide text-neutral-500">{PLAN_LABELS[c.plan_tier] || c.plan_tier}</span>
                      {c.mrr > 0 && (
                        <span className="font-mono font-semibold">${c.mrr}</span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 overflow-x-auto">
          <table className="lf-grid">
            <thead>
              <tr>
                <th>Business</th><th>Contact</th><th>Industry</th><th>Location</th>
                <th>Plan</th><th>MRR</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} data-testid={CLIENTS.row(c.id)}>
                  <td className="font-medium">{c.business_name}</td>
                  <td className="text-sm">{c.contact_name || "—"}</td>
                  <td className="text-sm">{c.industry || "—"}</td>
                  <td className="text-sm">{[c.city, c.state].filter(Boolean).join(", ")}</td>
                  <td className="text-sm font-mono uppercase">{PLAN_LABELS[c.plan_tier] || "—"}</td>
                  <td className="font-mono">{c.mrr ? `$${c.mrr}` : "—"}</td>
                  <td><span className={`pill pill-${c.status === "active" ? "closed_won" : c.status === "churned" ? "closed_lost" : c.status === "trial" ? "interested" : c.status === "paused" ? "website_sent" : "new"}`}>{c.status}</span></td>
                  <td>
                    <Link
                      to={`/clients/${c.id}`}
                      data-testid={CLIENTS.openBtn(c.id)}
                      className="inline-flex items-center gap-1 text-sm font-medium hover:underline"
                    >
                      Open <ExternalLink className="w-3 h-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <AddClientModal
          form={form}
          setForm={setForm}
          submitting={submitting}
          industries={industries}
          onClose={() => setShowForm(false)}
          onSubmit={submit}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="bg-white border border-neutral-200 p-5">
      <div className="overline mb-2">{label}</div>
      <div className={`font-display text-3xl font-bold ${accent ? "text-[#002FA7]" : ""}`}>{value}</div>
    </div>
  );
}
